// File: src/pages/ReportsPage.jsx

import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Page } from '@shopify/polaris';
import { ArrowLeftIcon } from '@shopify/polaris-icons';
import { AuthContext } from '../context/AuthContext';
import { SiteContext } from '../context/SiteContext';
import LayoutWrapper from '../components/Layout/Layout';
import '../components/Layout/ui-shell.css';
import {
  FileText,
  Download,
  Clock,
  AlertCircle,
  CheckCircle,
  Loader,
  Calendar,
  TrendingUp,
  Users,
  DollarSign,
  ShoppingCart,
  Trash2,
} from 'lucide-react';
import './ReportsPage.css';

const API = process.env.REACT_APP_API_URL || 'https://api.baobrain.com';

// ── Helpers ──
const formatMonth = (monthStr) => {
  if (!monthStr) return '';
  const [y, m] = monthStr.split('-');
  const date = new Date(Number(y), Number(m) - 1);
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
};

const getCurrentMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

const getAvailableMonths = () => {
  const months = [];
  const now = new Date();
  for (let i = 0; i < 6; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  }
  return months;
};


export default function ReportsPage() {
  const navigate = useNavigate();
  const { token } = useContext(AuthContext);
  const { activeSite } = useContext(SiteContext);

  const siteId = activeSite?.id || Number(sessionStorage.getItem('site_id')) || null;

  // State
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth());
  const [error, setError] = useState('');
  const [activeReport, setActiveReport] = useState(null);      // viewing a report
  const [generationProgress, setGenerationProgress] = useState(''); // status messages

  // ── Fetch existing reports ──
  const fetchReports = useCallback(async () => {
    if (!token || !siteId) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/reports/sales-intelligence/${siteId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to fetch reports');
      const data = await res.json();
      setReports(data);
    } catch (err) {
      console.error('[Reports] Fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [token, siteId]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  // ── Generate report ──
  const handleGenerate = async () => {
    if (!siteId || generating) return;

    setGenerating(true);
    setError('');
    setGenerationProgress('Collecting sales data from your store...');

    try {
      // Simulate progress updates
      const progressTimer = setTimeout(() => {
        setGenerationProgress('Analyzing orders, customers, and products...');
      }, 5000);
      const progressTimer2 = setTimeout(() => {
        setGenerationProgress('Generating insights with AI...');
      }, 15000);
      const progressTimer3 = setTimeout(() => {
        setGenerationProgress('Formatting your report...');
      }, 30000);

      const res = await fetch(`${API}/api/reports/sales-intelligence/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ site_id: siteId, month: selectedMonth }),
      });

      clearTimeout(progressTimer);
      clearTimeout(progressTimer2);
      clearTimeout(progressTimer3);

      const data = await res.json();

      if (!res.ok) {
        if (res.status === 409) {
          setError(data.detail || 'A report for this month already exists.');
        } else {
          setError(data.detail || 'Failed to generate report');
        }
        return;
      }

      // Success — show the report
      setActiveReport(data);
      await fetchReports(); // refresh the list
    } catch (err) {
      setError(`Generation failed: ${err.message}`);
    } finally {
      setGenerating(false);
      setGenerationProgress('');
    }
  };

  // ── View a report ──
  const handleViewReport = async (month) => {
    try {
      const res = await fetch(`${API}/api/reports/sales-intelligence/${siteId}/${month}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to load report');
      const data = await res.json();
      setActiveReport(data);
    } catch (err) {
      setError(`Failed to load report: ${err.message}`);
    }
  };

  // ── Download PDF ──
  const handleDownloadPDF = async (month) => {
    try {
      // Fetch the report HTML from the PDF endpoint (returns styled HTML)
      const res = await fetch(`${API}/api/reports/sales-intelligence/${siteId}/${month}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to download report');
      const html = await res.text();

      // Open in a new window and trigger print (Save as PDF)
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        setError('Pop-up blocked. Please allow pop-ups and try again.');
        return;
      }
      printWindow.document.write(html);
      printWindow.document.close();
      // Wait for content to render, then trigger print
      printWindow.onload = () => {
        setTimeout(() => printWindow.print(), 500);
      };
      // Fallback if onload doesn't fire
      setTimeout(() => printWindow.print(), 1500);
    } catch (err) {
      setError(`Download failed: ${err.message}`);
    }
  };

  // ── Delete report (for regeneration) ──
  const handleDeleteReport = async (month) => {
    if (!window.confirm(`Delete the ${formatMonth(month)} report? You can regenerate it after.`)) return;
    try {
      await fetch(`${API}/api/reports/sales-intelligence/${siteId}/${month}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      setActiveReport(null);
      await fetchReports();
    } catch (err) {
      setError(`Delete failed: ${err.message}`);
    }
  };

  // Check if selected month already has a report
  const existingReport = reports.find((r) => r.report_month === selectedMonth && r.status === 'complete');

  // ── RENDER: Report Viewer ──
  if (activeReport) {
    return (
      <LayoutWrapper>
        <Page
          title={activeReport.title || 'Sales Intelligence Report'}
          backAction={{
            content: 'Back to Reports',
            onAction: () => setActiveReport(null),
            icon: ArrowLeftIcon,
          }}
        >
          <div className="reports-page">
            <div className="report-viewer-header">
              <div className="report-viewer-actions">
                <button
                  className="report-action-btn"
                  onClick={() => handleDownloadPDF(activeReport.report_month)}
                >
                  <Download size={16} /> Download PDF
                </button>
                <button
                  className="report-action-btn danger"
                  onClick={() => handleDeleteReport(activeReport.report_month)}
                >
                  <Trash2 size={16} /> Delete & Regenerate
                </button>
              </div>
            </div>

            <div className="report-viewer-content">
              <div
                className="report-rendered-html"
                dangerouslySetInnerHTML={{ __html: activeReport.content_html }}
              />
              <div className="report-footer-note">
                Generated by BaoBrain Analytics • All revenue figures based on paid orders only
              </div>
            </div>
          </div>
        </Page>
      </LayoutWrapper>
    );
  }

  // ── RENDER: Reports List + Generator ──
  return (
    <LayoutWrapper>
      <Page
        title="Intelligence Reports"
        subtitle="AI-generated monthly analysis of your store's performance"
        backAction={{
          content: 'Back',
          onAction: () => navigate('/dashboard'),
          icon: ArrowLeftIcon,
        }}
      >
        <div className="reports-page">

      {/* Generate Card */}
      <div className="generate-card">
        <div className="generate-card-icon">
          <FileText size={32} />
        </div>
        <div className="generate-card-content">
          <h2>Sales Intelligence Report</h2>
          <p>
            Comprehensive analysis of your revenue, products, customers, repeat rate,
            traffic, and actionable recommendations — powered by AI.
          </p>

          <div className="generate-card-details">
            <div className="generate-detail">
              <TrendingUp size={14} />
              <span>Revenue & order analysis</span>
            </div>
            <div className="generate-detail">
              <Users size={14} />
              <span>Customer retention & segments</span>
            </div>
            <div className="generate-detail">
              <ShoppingCart size={14} />
              <span>Product performance & catalog audit</span>
            </div>
            <div className="generate-detail">
              <DollarSign size={14} />
              <span>Actionable growth recommendations</span>
            </div>
          </div>

          <div className="generate-controls">
            <div className="month-selector">
              <Calendar size={16} />
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                disabled={generating}
              >
                {getAvailableMonths().map((m) => (
                  <option key={m} value={m}>{formatMonth(m)}</option>
                ))}
              </select>
            </div>

            {existingReport ? (
              <button
                className="generate-btn view"
                onClick={() => handleViewReport(selectedMonth)}
              >
                <CheckCircle size={16} /> View Report
              </button>
            ) : (
              <button
                className="generate-btn"
                onClick={handleGenerate}
                disabled={generating || !siteId}
              >
                {generating ? (
                  <>
                    <Loader size={16} className="spin" /> Generating...
                  </>
                ) : (
                  <>
                    <FileText size={16} /> Generate Report
                  </>
                )}
              </button>
            )}
          </div>

          {generating && generationProgress && (
            <div className="generation-progress">
              <Loader size={14} className="spin" />
              <span>{generationProgress}</span>
            </div>
          )}

          {error && (
            <div className="generate-error">
              <AlertCircle size={14} />
              <span>{error}</span>
            </div>
          )}

          <p className="generate-note">
            One report per month per report type. Reports take 30-60 seconds to generate.
          </p>
        </div>
      </div>

      {/* Past Reports */}
      {reports.length > 0 && (
        <div className="past-reports">
          <h3 className="past-reports-title">Past Reports</h3>
          <div className="past-reports-list">
            {reports.map((r) => (
              <div key={r.id} className="past-report-row">
                <div className="past-report-info">
                  <div className="past-report-month">{formatMonth(r.report_month)}</div>
                  <div className="past-report-meta">
                    <Clock size={12} />
                    <span>
                      {r.created_at
                        ? new Date(r.created_at).toLocaleDateString('en-US', {
                            month: 'short', day: 'numeric', year: 'numeric',
                          })
                        : '—'}
                    </span>
                    {r.status === 'complete' && r.summary && (
                      <>
                        <span className="meta-dot">·</span>
                        <span>
                          {r.summary.currency_symbol}{Number(r.summary.total_revenue || 0).toLocaleString()} revenue
                        </span>
                        <span className="meta-dot">·</span>
                        <span>{r.summary.total_orders} orders</span>
                      </>
                    )}
                  </div>
                </div>
                <div className="past-report-actions">
                  <span className={`report-status-badge ${r.status}`}>
                    {r.status === 'complete' && <CheckCircle size={12} />}
                    {r.status === 'generating' && <Loader size={12} className="spin" />}
                    {r.status === 'failed' && <AlertCircle size={12} />}
                    {r.status}
                  </span>
                  {r.status === 'complete' && (
                    <>
                      <button className="past-report-btn" onClick={() => handleViewReport(r.report_month)}>
                        View
                      </button>
                      <button className="past-report-btn" onClick={() => handleDownloadPDF(r.report_month)}>
                        <Download size={14} />
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty state */}
      {!loading && reports.length === 0 && (
        <div className="reports-empty">
          <FileText size={40} />
          <p>No reports generated yet. Generate your first report above!</p>
        </div>
      )}
    </div>
      </Page>
    </LayoutWrapper>
  );
}